import { RouteLocation, RouteRecordRaw } from 'vue-router';
import { getCurrentEdition } from 'src/editions';

// convert all the route params to numeric values because the vue component props are all numeric
const toNumber = (route: RouteLocation) => {
  const params: { [key: string]: number } = {};
  Object.entries(route.params).forEach(
    (val) => (params[val[0]] = Number(val[1])),
  );
  return params;
};


const currentEdition = getCurrentEdition();

const defaultRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      { path: '', component: () => import('pages/HomePage.vue') },
      {
        path: '/unions',
        children: [
          { path: '', component: () => import('pages/Unions/UnionList.vue') },
          { path: 'add', component: () => import('pages/Unions/UnionAdd.vue') },
          {
            path: ':unionId',
            component: () => import('pages/Unions/UnionView.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/teams',
        children: [
          { path: '', component: () => import('pages/Teams/TeamList.vue') },
          { path: 'add', component: () => import('pages/Teams/TeamAdd.vue') },
          {
            path: ':teamId',
            component: () => import('pages/Teams/TeamView.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/events',
        children: [
          { path: '', component: () => import('pages/Events/EventList.vue') },
        ],
      },
      {
        path: '/assessments',
        children: [
          {
            path: '',
            component: () => import('pages/Assessments/AssessmentList.vue'),
          },
          {
            path: 'overdue',
            component: () => import('pages/Assessments/AssessmentOverdue.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/comprehensives',
        children: [
          {
            path: '',
            component: () =>
              import('pages/Comprehensives/ComprehensiveList.vue'),
          },
          {
            path: ':comprehensiveId',
            component: () =>
              import('pages/Comprehensives/ComprehensiveView.vue'),
            props: toNumber,
          },
          {
            path: 'rehab/:comprehensiveId',
            component: () =>
              import('pages/Comprehensives/ComprehensiveRehab.vue'),
            props: toNumber,
          },
          {
            path: 'add',
            component: () =>
              import('pages/Comprehensives/ComprehensiveAdd.vue'),
          },
          {
            path: 'player/:playerId',
            component: () =>
              import(
                'pages/Comprehensives/ComprehensiveListPlayerAssessments.vue'
              ),
            props: toNumber,
          },
        ],
      },
      {
        path: '/players',
        children: [
          { path: '', component: () => import('pages/Players/PlayerList.vue') },
          {
            path: 'add',
            component: () => import('pages/Players/PlayerAdd.vue'),
          },
          {
            path: ':playerId',
            component: () => import('pages/Players/PlayerView.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/users',
        children: [
          { path: '', component: () => import('pages/Users/UserList.vue') },
          {
            path: ':userId',
            component: () => import('pages/Users/UserView.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/tournaments',
        children: [
          {
            path: '',
            component: () => import('pages/Tournaments/TournamentList.vue'),
          },
          {
            path: 'add',
            component: () => import('pages/Tournaments/TournamentAdd.vue'),
          },
          {
            path: ':tournamentId',
            component: () => import('pages/Tournaments/TournamentView.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/venues',
        children: [
          { path: '', component: () => import('pages/Venues/VenueList.vue') },
          { path: 'add', component: () => import('pages/Venues/VenueAdd.vue') },
          {
            path: ':venueId',
            component: () => import('pages/Venues/VenueView.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/iccreviews',
        children: [
          {
            path: '',
            component: () => import('pages/ICCReviews/ICCReviewList.vue'),
          },
          {
            path: ':iccReviewId',
            component: () => import('pages/ICCReviews/ICCReviewView.vue'),
            props: toNumber,
          },
          {
            path: ':iccReviewId/decline',
            component: () => import('pages/ICCReviews/ICCReviewDecline.vue'),
            props: toNumber,
          },
        ],
      },
      {
        path: '/reports',
        component: () => import('pages/Reports/ReportList.vue'),
      },
      { path: '/about', component: () => import('pages/AboutScrm.vue') },
      { path: '/account', component: () => import('pages/MyAccount.vue') },
    ],
  },
  {
    path: '/auth',
    component: () => import('layouts/LoginLayout.vue'),
    children: [
      {
        path: 'lost-password',
        component: () => import('pages/Auth/LostPassword.vue'),
      },
      {
        path: 'set-password',
        component: () => import('pages/Auth/SetPassword.vue'),
      },
      {
        path: 'register',
        component: () => import('pages/Auth/UserRegister.vue'),
      },
      { path: 'gotoapp', component: () => import('pages/Auth/GoToApp.vue') },
      {
        path: ':pathMatch(.*)*',
        alias: ['login', '/login'],
        component: () => import('pages/Auth/UserLogin.vue'),
      },
    ],
  },
  {
    path: '/print',
    component: () => import('layouts/PrintLayout.vue'),
    children: [
      {
        path: 'comprehensive-outcome/:comprehensiveId',
        component: () =>
          import('pages/Comprehensives/ComprehensivePrintOutcome.vue'),
        props: toNumber,
      },
      {
        path: 'comprehensive-rehab/:comprehensiveId',
        component: () =>
          import('pages/Comprehensives/ComprehensivePrintRehab.vue'),
        props: toNumber,
      },
    ],
  },
  {
    path: '/external',
    component: () => import('layouts/ExternalLayout.vue'),
    children: [
      {
        path: 'player-review/:teamId',
        component: () => import('pages/External/PlayerReview.vue'),
        props: toNumber,
      },
    ],
  },
  // Always leave this as last one: show an error if the route is invalid
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorFourOhFour.vue'),
  },
];

function removeUnusedRoutes(
  routes: RouteRecordRaw[],
  routeFilter: string[]
): RouteRecordRaw[] {
  return routes.map(route => ({
    ...route,
    children: route.children?.filter(child => {
      const shouldKeep = !routeFilter.some(filter =>
        filter && child.path.includes(filter)
      );

      if (shouldKeep && child.children) {
        child.children = removeUnusedRoutes([child], routeFilter)[0].children;
      }

      return shouldKeep;
    })
  }));
}

const routes = removeUnusedRoutes(defaultRoutes, currentEdition.routeFilter);

export default routes;
